import type { MouseEvent, ReactNode } from 'react';
import { Plus } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { Checkbox, CheckboxVariant } from '../Checkbox';
import { isVideoUrl } from '@/utils/url';
import { Image } from '../Image';
import { VideoThumbnail } from '../VideoThumbnail';
import { Badge } from '../Badge';
import { cn } from '@/lib/utils';
import {
    QuickViewOverlay,
    type ThumbnailQuickViewState,
} from './QuickViewOverlay';

export type { ThumbnailQuickViewState } from './QuickViewOverlay';
export type ThumbnailSize = 'fluid' | 'sm' | 'md' | 'lg' | 'fill';
export type ThumbnailAddStyle = 'dashed' | 'filled';

export interface ThumbnailQuickViewProps {
    state?: ThumbnailQuickViewState;
    onClick?: (event: MouseEvent) => void;
    ariaLabel?: string;
    compact?: boolean;
}

type ImageVariantProps = {
    variant?: 'image';
    imageSrc?: string;
    media?: ReactNode;
    placeholderIcon?: ReactNode;
    title?: string;
    subtitle?: string;
    checked?: boolean;
    disabled?: boolean;
    size?: ThumbnailSize;
    imageUsesAuth?: boolean;
    showCheckbox?: boolean;
    checkboxVariant?: CheckboxVariant;
    showBorder?: boolean;
    showGradients?: boolean;
    centerIcon?: ReactNode;
    licensePlate?: string;
    quickView?: ThumbnailQuickViewProps;
    onClick?: () => void;
    className?: string;
    'data-testid'?: string;
};

type AddVariantProps = {
    variant: 'add';
    visualStyle?: ThumbnailAddStyle;
    icon?: ReactNode;
    'aria-label'?: string;
    compact?: boolean;
    onClick?: () => void;
    className?: string;
};

type PlaceholderVariantProps = {
    variant: 'placeholder';
    icon?: ReactNode;
    'aria-label'?: string;
    compact?: boolean;
    onClick?: () => void;
    className?: string;
};

export type ThumbnailProps =
    | ImageVariantProps
    | AddVariantProps
    | PlaceholderVariantProps;

const TILE_SIZE = {
    normal: 'h-[46px] w-[71px]',
    compact: 'w-16 aspect-video',
};

const TILE_BUTTON =
    'cursor-pointer p-0 text-left outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-grey-400 focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900';

const IMAGE_SIZE: Record<ThumbnailSize, string> = {
    fluid: 'w-full aspect-video rounded-[5px]',
    fill: 'h-full w-full min-h-0 min-w-0 rounded-[6px]',
    lg: 'w-[154px] h-[100px] rounded-[6px]',
    md: 'w-[98px] h-[64px] rounded-[4px]',
    sm: 'w-[71px] h-[46px] rounded-[2px]',
};

const BOTTOM_GRADIENT =
    'bg-[linear-gradient(179.78deg,rgba(0,0,0,0)_0.19%,rgba(0,0,0,0.75)_99.81%)]';

const TOP_GRADIENT =
    'bg-[linear-gradient(0.17deg,rgba(0,0,0,0)_2.91%,rgba(0,0,0,0.5)_98.27%)]';

function checkboxOffset(size: ThumbnailSize) {
    if (size === 'sm') {
        return 'top-1 right-1 group-hover/thumbnail:top-[3px] group-hover/thumbnail:right-[3px] group-data-[checked=true]/thumbnail:top-[3px] group-data-[checked=true]/thumbnail:right-[3px]';
    }
    return 'top-[5px] right-[5px] group-hover/thumbnail:top-1 group-hover/thumbnail:right-1 group-data-[checked=true]/thumbnail:top-1 group-data-[checked=true]/thumbnail:right-1';
}

function centerIconSvgSize(size: ThumbnailSize) {
    if (size === 'sm') return '[&_svg]:size-4';
    if (size === 'md') return '[&_svg]:size-5';
    return '[&_svg]:size-6';
}

function placeholderIconSize(size: ThumbnailSize) {
    if (size === 'sm') return 'size-4';
    if (size === 'md') return 'size-5';
    if (size === 'lg') return 'size-6';
    return 'size-8';
}

export function Thumbnail(props: ThumbnailProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);

    if (props.variant === 'add') {
        const {
            visualStyle = 'dashed',
            icon,
            compact = false,
            onClick,
            className,
            'aria-label': ariaLabel = 'Add',
        } = props;

        const tileClass = cn(
            'box-border flex shrink-0 items-center justify-center rounded-[2px] border border-dashed border-grey-400',
            compact ? TILE_SIZE.compact : TILE_SIZE.normal,
            visualStyle === 'filled' && 'bg-grey-600',
        );

        const content = icon ?? (
            <Plus
                weight="regular"
                className={cn(
                    compact ? 'size-3.5' : 'size-4',
                    visualStyle === 'dashed'
                        ? 'text-grey-200'
                        : 'text-grey-100',
                )}
                aria-hidden
            />
        );

        if (onClick) {
            return (
                <button
                    type="button"
                    aria-label={ariaLabel}
                    onClick={onClick}
                    className={cn(tileClass, TILE_BUTTON, className)}
                >
                    {content}
                </button>
            );
        }

        return (
            <div className={cn(tileClass, className)} aria-hidden>
                {content}
            </div>
        );
    }

    if (props.variant === 'placeholder') {
        const {
            icon,
            compact = false,
            onClick,
            className,
            'aria-label': ariaLabel = 'Placeholder',
        } = props;

        const tileClass = cn(
            'box-border flex shrink-0 items-center justify-center rounded-[2px] bg-grey-550',
            compact ? TILE_SIZE.compact : TILE_SIZE.normal,
        );

        const content = icon ?? (
            <SecurityCameraIconMirrored
                weight="fill"
                className={cn('text-grey-400', compact ? 'size-3.5' : 'size-4')}
                aria-hidden
            />
        );

        if (onClick) {
            return (
                <button
                    type="button"
                    aria-label={ariaLabel}
                    onClick={onClick}
                    className={cn(tileClass, TILE_BUTTON, className)}
                >
                    {content}
                </button>
            );
        }

        return (
            <div
                role="img"
                aria-label={ariaLabel}
                className={cn(tileClass, className)}
            >
                {content}
            </div>
        );
    }

    const {
        imageSrc,
        media,
        placeholderIcon,
        title,
        subtitle,
        checked = false,
        disabled = false,
        onClick,
        size = 'fluid',
        imageUsesAuth = true,
        showCheckbox = false,
        checkboxVariant = CheckboxVariant.DEFAULT,
        showBorder = false,
        showGradients = false,
        centerIcon,
        licensePlate,
        quickView,
        className,
        'data-testid': testId,
    } = props;

    return (
        <div
            data-testid={testId}
            data-checked={checked ? 'true' : 'false'}
            className={cn(
                'group/thumbnail relative overflow-hidden outline-none transition-colors',
                disabled
                    ? 'cursor-not-allowed opacity-40'
                    : showCheckbox || onClick
                      ? 'cursor-pointer'
                      : 'cursor-inherit',
                IMAGE_SIZE[size],
                showBorder &&
                    (showCheckbox
                        ? 'border border-grey-500 hover:border-grey-400'
                        : 'border border-grey-500'),
                !showBorder && 'border-0',
                className,
            )}
            aria-disabled={disabled || undefined}
            onClick={disabled ? undefined : onClick}
        >
            <div className="absolute inset-0 min-h-0 min-w-0">
                {media != null ? (
                    <div className="relative h-full w-full min-h-0 min-w-0">
                        {media}
                    </div>
                ) : imageSrc != null && isVideoUrl(imageSrc) ? (
                    <VideoThumbnail
                        src={imageSrc}
                        className="pointer-events-none h-full w-full object-cover"
                    />
                ) : imageSrc != null ? (
                    <Image
                        src={imageSrc}
                        alt={title || licensePlate || 'Thumbnail'}
                        auth={imageUsesAuth}
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div
                        className="flex h-full w-full items-center justify-center bg-grey-550"
                        aria-hidden
                    >
                        {placeholderIcon ?? (
                            <SecurityCameraIconMirrored
                                weight="fill"
                                className={cn(
                                    'text-grey-400',
                                    placeholderIconSize(size),
                                )}
                            />
                        )}
                    </div>
                )}
            </div>

            {showGradients && (
                <>
                    <div
                        className={cn(
                            'pointer-events-none absolute inset-0',
                            BOTTOM_GRADIENT,
                        )}
                        aria-hidden
                    />
                    <div
                        className={cn(
                            'pointer-events-none absolute inset-0 transition-opacity duration-150',
                            TOP_GRADIENT,
                            showCheckbox && 'group-hover/thumbnail:opacity-0',
                            'group-data-[checked=true]/thumbnail:opacity-0',
                        )}
                        aria-hidden
                    />
                </>
            )}

            {centerIcon && (
                <div className="pointer-events-none absolute inset-0 z-[24] flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-[rgba(17,18,20,0.7)]"
                        aria-hidden
                    />
                    <div
                        className={cn(
                            'relative z-[1] flex items-center justify-center text-white-100',
                            centerIconSvgSize(size),
                        )}
                    >
                        {centerIcon}
                    </div>
                </div>
            )}

            {licensePlate ? (
                <div className="pointer-events-none absolute bottom-1 right-1 z-[21]">
                    <Badge variant="license-plate">{licensePlate}</Badge>
                </div>
            ) : null}

            {showCheckbox && (
                <div className={cn('absolute z-[22]', checkboxOffset(size))}>
                    <Checkbox
                        checked={checked}
                        variant={checkboxVariant}
                        className="pointer-events-none"
                    />
                </div>
            )}

            {(title || subtitle) && size !== 'sm' && size !== 'md' && (
                <div className="pointer-events-none absolute inset-x-[5px] bottom-[5px] z-[17] flex flex-col">
                    {title && (
                        <span className="w-full truncate text-sm font-medium leading-5 text-white-100">
                            {title}
                        </span>
                    )}
                    {subtitle && (
                        <span className="w-full truncate text-xs font-normal leading-[18px] text-grey-100">
                            {subtitle}
                        </span>
                    )}
                </div>
            )}

            {quickView && (
                <QuickViewOverlay
                    state={quickView.state ?? 'idle'}
                    onClick={quickView.onClick}
                    ariaLabel={quickView.ariaLabel ?? t('button.quickView')}
                    compact={quickView.compact}
                />
            )}
        </div>
    );
}
